import { useMutation, useQueryClient } from "@tanstack/react-query";
import type React from "react";
import { useCallback, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { uploadDocument } from "@/api/documentApi";
import type { FileValidationResult } from "@/types";
import { validateFile } from "@/utils/fileValidation";

interface UseFileUploadOptions {
	onSuccess?: () => void;
}

export function useFileUpload({ onSuccess }: UseFileUploadOptions = {}) {
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const [selectedFile, setSelectedFile] = useState<File | null>(null);
	const [validationResult, setValidationResult] = useState<FileValidationResult | null>(null);
	const [isDragging, setIsDragging] = useState(false);
	const fileInputRef = useRef<HTMLInputElement>(null);

	const reset = useCallback(() => {
		setSelectedFile(null);
		setValidationResult(null);
		if (fileInputRef.current) fileInputRef.current.value = "";
	}, []);

	const uploadMutation = useMutation({
		mutationFn: (file: File) =>
			uploadDocument(file, {
				extractEntities: true,
				extractRelationships: true,
				generateFormal: true,
				validateExpressions: true,
			}),
		onSuccess: () => {
			reset();
			queryClient.invalidateQueries({ queryKey: ["documents"] });
			navigate("/app");
			onSuccess?.();
		},
	});

	const handleFileSelect = useCallback((file: File) => {
		const validation = validateFile(file);
		setValidationResult(validation);
		setSelectedFile(validation.valid ? file : null);
	}, []);

	const handleFileInputChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			const file = e.target.files?.[0];
			if (file) handleFileSelect(file);
		},
		[handleFileSelect],
	);

	const handleDrop = useCallback(
		(e: React.DragEvent<HTMLDivElement>) => {
			e.preventDefault();
			setIsDragging(false);
			const file = e.dataTransfer.files[0];
			if (file) handleFileSelect(file);
		},
		[handleFileSelect],
	);

	const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
		e.preventDefault();
		setIsDragging(true);
	}, []);

	const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
		e.preventDefault();
		setIsDragging(false);
	}, []);

	const handleBrowseClick = useCallback(() => {
		fileInputRef.current?.click();
	}, []);

	const handleUpload = useCallback(() => {
		if (selectedFile && validationResult?.valid) {
			uploadMutation.mutate(selectedFile);
		}
	}, [selectedFile, validationResult, uploadMutation]);

	return {
		selectedFile,
		validationResult,
		isDragging,
		fileInputRef,
		uploadMutation,
		reset,
		handleFileSelect,
		handleFileInputChange,
		handleDrop,
		handleDragOver,
		handleDragLeave,
		handleBrowseClick,
		handleUpload,
	};
}
